import { twMerge } from 'tailwind-merge';

import { twSort, useBoolean } from '@/_shared';

import type { InputProps } from './type';

const baseInputStyle = twSort`peer border-c-border text-c-card-foreground placeholder-shown:border-c-border focus:border-primary disabled:border-c-border-muted h-full w-full border-b bg-transparent pt-4 pb-2.5 pl-0.5 font-sans text-sm font-normal text-ellipsis outline-0 transition-all placeholder:opacity-0 focus:outline-0 focus:placeholder:opacity-100 disabled:bg-transparent`;

export const Input = (
  // !important
  // shrink = true intentionally set to true to make the label always shrink
  {
    onAnimationStart,
    containerProps = {},
    label,
    labelProps,
    shrink = true,
    className,
    preSlotProps = {},
    preSlot,
    postSlotProps = {},
    postSlot,
    ...props
  }: InputProps
) => {
  const [isFilled, filled] = useBoolean();
  const { className: containerClassName, ...restContainerProps } = containerProps;

  return (
    <section
      {...restContainerProps}
      className={twMerge('relative flex h-11 w-full min-w-20 items-end', containerClassName)}
    >
      {preSlot && (
        <div
          {...preSlotProps}
          className={twMerge(
            'border-c-border border-b pr-1 pb-1',
            props.disabled && 'border-c-border-muted',
            preSlotProps.className
          )}
        >
          {preSlot}
        </div>
      )}
      <input
        {...props}
        className={twMerge(baseInputStyle, className)}
        placeholder={props.placeholder ?? ' '}
        onAnimationStart={(e) => {
          onAnimationStart?.(e);
          if (e.animationName === 'auto-fill') {
            filled.setTrue();
          }
        }}
      />

      <label
        className={twMerge(
          'text-c-card-muted peer-focus:text-primary peer-focus:after:border-primary dark:peer-focus:text-primary pointer-events-none absolute -top-1.5 left-0 flex h-full w-full transform-gpu truncate text-[11px] leading-tight font-normal transition-all select-none peer-focus:text-[11px] peer-focus:leading-tight peer-focus:after:scale-x-100',
          "after:border-primary after:content[''] after:absolute after:-bottom-1.5 after:block after:w-full after:scale-x-0 after:border-b-2 after:transition-transform after:duration-300",
          'peer-placeholder-shown:text-c-card-muted peer-placeholder-shown:peer-disabled:text-c-card-muted peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.25] dark:peer-placeholder-shown:text-gray-400',
          'peer-disabled:text-c-card-muted dark:peer-disabled:text-gray-400',
          'overflow-visible!',
          (shrink || isFilled) && 'text-[11px]! leading-tight!',
          labelProps?.className
        )}
      >
        <div className="leading-none text-wrap">{label}</div>
      </label>
      {postSlot && (
        <div
          {...postSlotProps}
          className={twMerge(
            'border-c-border border-b pb-1 pl-1',
            props.disabled && 'border-c-border/40',
            postSlotProps.className
          )}
        >
          {postSlot}
        </div>
      )}
    </section>
  );
};
